import FalconCloseButton from '@/components/common/FalconCloseButton';

import { Modal } from '@/components/common/AppModal';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  hidegapReportModal,
  showgapReportModal
} from '@/app/KeywordResearch/reducers/gapReportModal';
import Select, {
  type MultiValue,
  type OptionProps,
  type StylesConfig
} from 'react-select';
import * as yup from 'yup';
import {
  intentMap,
  purposeMap
} from '@/app/KeywordResearch/config/keywordGapColumnConfig';
import classNames from 'classnames';
import { Formik } from 'formik';
import { useSendGapReportMutation } from '@/app/KeywordResearch/api/gapApi';
import { errorMessage, successMessage } from '@/utils/toast';

type GapReportModalState = {
  keywords?: string;
  intent?: string[];
  purpose?: string[];
};

type GapReportRootState = {
  gapReportModal: GapReportModalState;
};

type ReportSelectOption = { label: string; value: string };

type GapReportFormValues = {
  intent: ReportSelectOption[];
  purpose: ReportSelectOption[];
};

const formikArrayFieldMessage = (error: unknown): string | undefined =>
  typeof error === 'string' ? error : undefined;

const gapReportSelectStyles = (
  showInvalidBorder: boolean
): StylesConfig<ReportSelectOption, true> => ({
  menu: () => ({
    position: 'absolute',
    zIndex: 9999,
    width: '100%'
  }),
  control: baseStyles => ({
    ...baseStyles,
    direction: 'rtl',
    paddingLeft: '10px',
    paddingRight: '10px',
    color: 'var(--falcon-emphasise)',
    boxShadow: 'none',
    borderRadius: '5px',
    width: '100%',
    cursor: 'pointer',
    borderColor: showInvalidBorder
      ? 'var(--falcon-form-invalid-border-color)'
      : baseStyles.borderColor
  }),
  input: baseStyles => ({
    ...baseStyles,
    direction: 'ltr',
    color: 'var(--falcon-emphasise)',
    boxShadow: 'none'
  })
});

function GapReportModal() {
  const dispatch = useDispatch();
  const gapReportModal = useSelector(
    (state: GapReportRootState) => state.gapReportModal
  );
  const [sendGapReport, { isLoading }] = useSendGapReportMutation();

  const initialIntent =
    gapReportModal?.intent?.map(intent => ({
      label: intent,
      value: intent
    })) ?? [];
  const initialPurpose =
    gapReportModal?.purpose?.map(purpose => ({
      label: purposeMap[purpose] ?? purpose,
      value: purpose
    })) ?? [];

  return (
    <Formik<GapReportFormValues>
      onSubmit={({ purpose, intent }, actions) => {
        sendGapReport({
          keywords: gapReportModal?.keywords,
          purpose: purpose.map(({ value }) => value),
          intent: intent.map(({ value }) => value)
        })
          .then(() => successMessage('گزارش با موفقیت ارسال شد.'))
          .catch(() => errorMessage('خطا در ارسال گزارش'))
          .finally(() => {
            dispatch(hidegapReportModal());
            actions.resetForm();
          });
      }}
      validationSchema={yup.object().shape({
        intent: yup.array().min(1, 'حداقل یک نیت وارد کن.'),
        purpose: yup.array().min(1, 'حداقل یک هدف وارد کن.')
      })}
      initialValues={{
        intent: initialIntent,
        purpose: initialPurpose
      }}
      enableReinitialize
    >
      {({
        setFieldValue,
        values,
        handleSubmit,
        resetForm,
        errors,
        touched
      }) => (
        <Modal
          show={Boolean(gapReportModal?.keywords)}
          onHide={() => dispatch(showgapReportModal())}
          backdrop="static"
          size="lg"
          keyboard={false}
        >
          <>
            <Modal.Header>
              <Modal.Title> گزارش پیشنهاد </Modal.Title>
              <FalconCloseButton
                onClick={() => dispatch(hidegapReportModal())}
              />
            </Modal.Header>
            <Modal.Body>
              <div className="fs--1">
                لطفا پیشنهاد خودتون رو برای کلمه{' '}
                <span className="fw-black">
                  {decodeURIComponent(gapReportModal?.keywords ?? '')}
                </span>{' '}
                انتخاب و ارسال کنید.
              </div>
              <Row>
                <Col>
                  <Form.Group className="my-3">
                    <Form.Label>هدف پیشنهادی </Form.Label>
                    <Select<ReportSelectOption, true>
                      classNamePrefix="react-select"
                      placeholder="انتخاب کن"
                      styles={gapReportSelectStyles(
                        Boolean(errors.purpose && touched.purpose)
                      )}
                      components={{
                        Option: OptionLayout
                      }}
                      onChange={(e: MultiValue<ReportSelectOption>) => {
                        setFieldValue('purpose', e ?? []);
                      }}
                      isMulti
                      value={values.purpose}
                      isSearchable={true}
                      noOptionsMessage={() => 'یافت نشد'}
                      options={Object.entries(purposeMap).map(([k, v]) => ({
                        label: v,
                        value: k
                      }))}
                    />
                    <Form.Control.Feedback
                      className="d-block"
                      type={errors.purpose ? 'invalid' : 'valid'}
                    >
                      {touched.purpose &&
                        formikArrayFieldMessage(errors.purpose)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="my-3">
                    <Form.Label>نیت پیشنهادی </Form.Label>
                    <Select<ReportSelectOption, true>
                      classNamePrefix="react-select"
                      placeholder="انتخاب کن"
                      styles={gapReportSelectStyles(
                        Boolean(errors.intent && touched.intent)
                      )}
                      components={{
                        Option: OptionLayout
                      }}
                      onChange={(e: MultiValue<ReportSelectOption>) => {
                        setFieldValue('intent', e ?? []);
                      }}
                      isMulti
                      value={values.intent}
                      isSearchable={true}
                      noOptionsMessage={() => 'یافت نشد'}
                      options={Object.entries(intentMap).map(([k, v]) => ({
                        label: k,
                        value: k
                      }))}
                    />
                    <Form.Control.Feedback
                      className="d-block"
                      type={errors.intent ? 'invalid' : 'valid'}
                    >
                      {touched.intent && formikArrayFieldMessage(errors.intent)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  dispatch(hidegapReportModal());
                  resetForm();
                }}
              >
                لغو
              </Button>
              <Button
                variant="falcon-primary"
                onClick={() => {
                  handleSubmit();
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      style={{
                        width: '16px',
                        height: '16px'
                      }}
                    />
                    درحال ارسال
                  </>
                ) : (
                  'ارسال گزارش'
                )}
              </Button>
            </Modal.Footer>
          </>
        </Modal>
      )}
    </Formik>
  );
}
export default GapReportModal;

const OptionLayout = (props: OptionProps<ReportSelectOption, true>) => {
  const { innerProps, innerRef } = props;
  return (
    <>
      <style>{`
          .customized-searchbox-option:hover{
            background-color:var(--falcon-gray-link-hover-300) !important;
          }
          .customized-searchbox-option{
            background-color:var(--falcon-tertiary-bg) !important;
          }
          `}</style>
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          backgroundColor: props.isSelected
            ? 'var(--falcon-gray-link-hover-100)'
            : 'var(--falcon-dropdown-bg-global)'
        }}
        className={classNames(
          'p-2 cursor-pointer border border-1 customized-searchbox-option',
          {}
        )}
      >
        <div className="fs-0 text-900">{props.data.label}</div>
      </div>
    </>
  );
};
